import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { isEmptyArray } from '@vista-digital/occ-domain';
import { useDebounceWindowEventListener, useMountEffect } from '@vista-digital/react-tools';
import debounce from 'debounce';
import { observer } from 'mobx-react';
import { createRef, useMemo, useRef, useState } from 'react';
import { ScrollDirection } from '../../types/scroll-direction';
import { deviceHasTouch } from '../../utils/device-has-touch';
import { scrollElementClientWidth, scrollElementIntoView } from '../../utils/imperative-utils/scroll';
import { Icon } from '../icon';
import { Tab } from './subcomponents/tab';
import { TabsDisplayType } from './types/display-type';
import { getCurrentTabScrollState, initialTabScrollState } from './utils/scroll-state';
export const defaultDebounceInterval = 166;
const b = bemClassBuilder('v-tabs');
export const Tabs = observer(props => {
    const { tabList, selectedTab, onTabSelected, centered, displayType } = props;
    const tabListRef = useRef(null);
    const tabRefs = useMemo(() => {
        return tabList.reduce((acc, tab) => {
            acc[tab.value] = createRef();
            return acc;
        }, {});
    }, [tabList]);
    const leftScrollButtonRef = useRef(null);
    const rightScrollButtonRef = useRef(null);
    const [scrollState, setScrollState] = useState(initialTabScrollState);
    /* Update states which depend on refs. */
    useMountEffect(() => updateScrollState(selectedTab));
    useDebounceWindowEventListener(() => updateScrollState(selectedTab), 'resize', defaultDebounceInterval);
    if (isEmptyArray(tabList))
        return null;
    const handleTabClicked = (tab) => {
        onTabSelected(tab);
        updateScrollState(tab);
    };
    const updateScrollState = (tabToShow) => {
        var _a;
        const tabListElement = tabListRef.current;
        if (!tabListElement)
            return;
        setScrollState(getCurrentTabScrollState(tabListElement));
        if (!tabToShow)
            return;
        const tabElement = (_a = tabRefs[tabToShow.value]) === null || _a === void 0 ? void 0 : _a.current;
        if (tabElement)
            scrollElementIntoView(tabElement, tabListElement, getScrollBoundary());
    };
    const getScrollBoundary = () => {
        var _a, _b, _c, _d;
        return {
            left: (_b = (_a = leftScrollButtonRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().right) !== null && _b !== void 0 ? _b : undefined,
            right: (_d = (_c = rightScrollButtonRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().left) !== null && _d !== void 0 ? _d : undefined
        };
    };
    const handleScrollButtonClicked = (direction) => {
        if (tabListRef.current)
            scrollElementClientWidth(tabListRef.current, direction);
    };
    const { canScrollLeft, canScrollRight } = scrollState;
    const showScrollButtons = !deviceHasTouch();
    const showBottomBorder = displayType === TabsDisplayType.Primary;
    return (_jsx("div", { className: b.block(), children: _jsxs("header", { className: b.element('header'), children: [showScrollButtons && (_jsx("button", { className: b.element('scroll-button', { left: true }), onClick: () => handleScrollButtonClicked(ScrollDirection.Left), disabled: !canScrollLeft, ref: leftScrollButtonRef, children: _jsx(Icon, { name: "chevron-left" }) })), _jsxs("div", { className: b.element('wrapper', { canScrollLeft, canScrollRight, showScrollButtons }), children: [showBottomBorder && _jsx("div", { className: b.element('bottom-border') }), _jsx("ul", { className: b.element('tab-list', { centered, type: displayType }), role: "tabList", ref: tabListRef, onScroll: debounce(() => updateScrollState(), defaultDebounceInterval), children: tabList.map(tab => {
                                return (_jsx(Tab, { tab: tab, selected: selectedTab.value === tab.value, onClick: handleTabClicked, displayType: displayType, ref: tabRefs[tab.value] }, tab.value));
                            }) })] }), showScrollButtons && (_jsx("button", { className: b.element('scroll-button', { right: true }), onClick: () => handleScrollButtonClicked(ScrollDirection.Right), disabled: !canScrollRight, ref: rightScrollButtonRef, children: _jsx(Icon, { name: "chevron-right" }) }))] }) }));
});
Tabs.displayName = 'Tabs';
//# sourceMappingURL=tabs.js.map